import type { ScheduleDetail, ScheduleListItem } from './appService';

export type ScheduleAttendanceRecordStatus = NonNullable<ScheduleDetail['attendance']>['records'][number]['status'];

export interface ScheduleMonthGroup {
  readonly label: string;
  readonly schedules: readonly ScheduleListItem[];
}

export interface UtcMonthDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

export interface ScheduleDateChip {
  readonly day: string;
  readonly weekday: string;
}

export interface ScheduleDateWindow {
  readonly from: string;
  readonly to: string;
}

const WEEKDAY_LABELS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'] as const;

export function sortSchedulesByStart(schedules: readonly ScheduleListItem[]): readonly ScheduleListItem[] {
  return [...schedules].sort((left, right) => {
    const startComparison = parseScheduleMillis(left.startsAt) - parseScheduleMillis(right.startsAt);
    if (startComparison !== 0) {
      return startComparison;
    }

    const titleComparison = compareText(left.title, right.title);
    if (titleComparison !== 0) {
      return titleComparison;
    }

    return compareText(left.scheduleId, right.scheduleId);
  });
}

export function groupSchedulesByMonth(schedules: readonly ScheduleListItem[]): readonly ScheduleMonthGroup[] {
  const groups = new Map<string, ScheduleListItem[]>();
  for (const schedule of schedules) {
    const key = formatScheduleMonthLabel(getScheduleDateKey(schedule.startsAt));
    const group = groups.get(key) ?? [];
    group.push(schedule);
    groups.set(key, group);
  }

  return Array.from(groups.entries()).map(([label, groupSchedules]) => ({
    label,
    schedules: groupSchedules
  }));
}

export function groupSchedulesByDate(
  schedules: readonly ScheduleListItem[]
): ReadonlyMap<string, readonly ScheduleListItem[]> {
  const groups = new Map<string, ScheduleListItem[]>();
  for (const schedule of schedules) {
    const key = getScheduleDateKey(schedule.startsAt);
    const group = groups.get(key) ?? [];
    group.push(schedule);
    groups.set(key, group);
  }
  return groups;
}

export function buildCalendarDateKeys(year: number, month: number): readonly string[] {
  const firstDay = new Date(Date.UTC(year, month - 1, 1));
  const firstGridDay = 1 - firstDay.getUTCDay();
  return Array.from({ length: 42 }, (_, index) =>
    formatDateKey(new Date(Date.UTC(year, month - 1, firstGridDay + index)))
  );
}

export function getFirstScheduleDate(schedules: readonly ScheduleListItem[]): string | null {
  return schedules[0] ? getScheduleDateKey(schedules[0].startsAt) : null;
}

export function getScheduleDateKey(isoTimestamp: string): string {
  return formatDateKey(getUtcDate(isoTimestamp));
}

export function getUtcMonth(isoTimestamp: string): number {
  return getUtcDate(isoTimestamp).getUTCMonth() + 1;
}

export function getUtcDay(isoTimestamp: string): number {
  return getUtcDate(isoTimestamp).getUTCDate();
}

export function getUtcMonthDate(dateKey: string): UtcMonthDate {
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    throw new Error(`Invalid schedule date key: ${dateKey}`);
  }
  return { year, month, day };
}

export function formatScheduleMonthKey(dateKey: string): string {
  const date = getUtcMonthDate(dateKey);
  return `${date.year}-${String(date.month).padStart(2, '0')}`;
}

export function getAdjacentScheduleMonth(monthKey: string, offset: -1 | 1): string {
  const [year, month] = monthKey.split('-').map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month)) {
    throw new Error(`Invalid schedule month key: ${monthKey}`);
  }

  const date = new Date(Date.UTC(year, month - 1 + offset, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function getScheduleVisibleDateWindow(monthKey: string): ScheduleDateWindow {
  const [year, month] = monthKey.split('-').map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month)) {
    throw new Error(`Invalid schedule month key: ${monthKey}`);
  }

  return {
    from: formatDateKey(new Date(Date.UTC(year, month - 2, 1))),
    to: formatDateKey(new Date(Date.UTC(year, month + 1, 0)))
  };
}

export function getScheduleMonthDateWindow(monthKey: string): ScheduleDateWindow {
  const [year, month] = monthKey.split('-').map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month)) {
    throw new Error(`Invalid schedule month key: ${monthKey}`);
  }

  return {
    from: formatDateKey(new Date(Date.UTC(year, month - 1, 1))),
    to: formatDateKey(new Date(Date.UTC(year, month, 0)))
  };
}

export function formatDateKey(date: Date): string {
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid schedule date');
  }
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate()
  ).padStart(2, '0')}`;
}

export function formatScheduleMonthLabel(dateKey: string): string {
  const date = getUtcMonthDate(dateKey);
  return `${date.year}年${date.month}月`;
}

export function formatScheduleFullDate(dateKey: string): string {
  const date = getUtcMonthDate(dateKey);
  return `${date.year}年${date.month}月${date.day}日`;
}

export function formatScheduleDateChip(isoTimestamp: string): ScheduleDateChip {
  const date = getUtcDate(isoTimestamp);
  return {
    day: String(date.getUTCDate()),
    weekday: WEEKDAY_LABELS[date.getUTCDay()]
  };
}

export function formatScheduleRowMeta(schedule: ScheduleListItem): string {
  const parts = [formatScheduleTimeRange(schedule), schedule.location];
  if (schedule.attendanceCategoryName) {
    parts.push(schedule.attendanceCategoryName);
  }
  parts.push(formatScheduleAttendanceStatus(schedule.attendanceStatus));
  return parts.join(' · ');
}

export function formatScheduleAttendanceStatus(status: ScheduleListItem['attendanceStatus']): string {
  const labels: Record<ScheduleListItem['attendanceStatus'], string> = {
    not_enabled: '未启用考勤',
    not_started: '未开始',
    signing: '签到中',
    ended: '考勤已截止'
  };
  return labels[status];
}

export function getScheduleStatusBoundaryRefreshMs(
  schedules: readonly ScheduleListItem[],
  nowMillis: number = Date.now()
): number | false {
  let nextBoundaryMillis: number | null = null;

  for (const schedule of schedules) {
    for (const boundaryMillis of [
      parseScheduleMillis(schedule.startsAt),
      parseScheduleMillis(schedule.endsAt)
    ] as const) {
      if (boundaryMillis <= nowMillis) {
        continue;
      }
      nextBoundaryMillis = nextBoundaryMillis === null ? boundaryMillis : Math.min(nextBoundaryMillis, boundaryMillis);
    }
  }

  return nextBoundaryMillis === null ? false : Math.max(1, nextBoundaryMillis - nowMillis);
}

export function formatScheduleDetailTime(schedule: ScheduleListItem): string {
  return `${formatScheduleFullDate(getScheduleDateKey(schedule.startsAt))} ${formatScheduleTimeRange(schedule)}`;
}

export function formatParticipantGroupSummary(memberGroupIds: readonly string[]): string {
  return memberGroupIds.length === 0 ? '全团' : `${memberGroupIds.length} 个分组`;
}

export function formatAttendanceRecordStatus(status: ScheduleAttendanceRecordStatus): string {
  const labels: Record<ScheduleAttendanceRecordStatus, string> = {
    pending: '待签到',
    on_time: '准时',
    late: '迟到',
    absent: '缺勤'
  };
  return labels[status];
}

export function formatAttendanceSelectedTime(selectedAt: string | null): string {
  return selectedAt ? formatScheduleTime(selectedAt) : '未选择';
}

function formatScheduleTimeRange(schedule: ScheduleListItem): string {
  return `${formatScheduleTime(schedule.startsAt)}-${formatScheduleTime(schedule.endsAt)}`;
}

function formatScheduleTime(isoTimestamp: string): string {
  const date = getUtcDate(isoTimestamp);
  return `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
}

function parseScheduleMillis(isoTimestamp: string): number {
  return getUtcDate(isoTimestamp).getTime();
}

function getUtcDate(isoTimestamp: string): Date {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid schedule timestamp: ${isoTimestamp}`);
  }
  return date;
}

function compareText(left: string, right: string): number {
  if (left < right) {
    return -1;
  }
  if (left > right) {
    return 1;
  }
  return 0;
}
